// Apolo IDE — LiveActivity.
//
// Indicador VIVO do agent loop: em vez de um pill estático "Agente
// trabalhando…", mostra a FASE atual (pensando / gerando / executando tool) +
// um cronômetro correndo. É o sinal de que o modelo NÃO está em silêncio mesmo
// quando ainda não chegou nenhum token (ex.: reasoning model "pensando" antes
// de responder). O streaming de tokens/reasoning entra na própria bolha; este
// pill cobre o intervalo até o primeiro delta e o tempo entre passos.

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getRunStartedAt } from '../../../lib/agentRunStore.js';

const PHRASES = {
  pensando: 'Pensando…',
  gerando: 'Gerando resposta…',
  tool: 'Executando ferramenta',
};

// Dogfood 2026-07-10 (fix I fora do DAG) — o caminho plan/free-form era um pill
// mudo ("Executando…") sem noção de progresso; o D1 mostrava o loop 5 passos à
// frente da tela. O loop JÁ emite loop_step/tool_call — este redutor puro (testável)
// acumula {step, tool} pro pill mostrar "Passo N · Executando tool…".
export function advanceLiveStatus(prev, evt) {
  if (!evt) return prev;
  if (evt.type === 'loop_step') return { ...(prev || { phase: 'pensando' }), step: evt.n };
  if (evt.type === 'tool_call') return { phase: 'tool', tool: evt.name, step: prev?.step };
  return prev;
}

function fmt(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function LiveActivity({ status, sessionId }) {
  const [elapsed, setElapsed] = useState(0);
  // HRN3-T04 — start time from the run store so the timer survives a remount
  // (tab switch / session change). Fall back to mount time when not in the store.
  const startRef = useRef(null);
  if (startRef.current == null) startRef.current = Date.now();
  const startedAt = (sessionId && getRunStartedAt(sessionId)) || startRef.current;

  useEffect(() => {
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const phase = status?.phase || 'pensando';
  const stepPrefix = Number.isFinite(Number(status?.step)) && status?.step != null ? `Passo ${status.step} · ` : '';
  const label = stepPrefix
    + (phase === 'tool' && status?.tool ? `Executando ${status.tool}…` : PHRASES[phase] || 'Trabalhando…');

  return (
    <>
      {/* Visual pill — aria-hidden so the per-second timer is NOT re-announced
          by a screen reader every tick (HRN3-T03). */}
      <div
        aria-hidden="true"
        className="inline-flex items-center gap-2 self-start rounded-full border px-3 py-1.5 text-xs text-apolo-textSecondary"
        style={{ borderColor: 'var(--rx-line)', background: 'var(--rx-surface-1)', boxShadow: 'none' }}
      >
        <Loader2 size={12} className="animate-spin text-apolo-accent" aria-hidden="true" />
        <span>{label}</span>
        <span className="font-mono tabular-nums text-[11px] text-apolo-textTertiary">{fmt(elapsed)}</span>
      </div>
      {/* The ONE dedicated polite announcer: phase only. Its text changes when the
          phase changes (not every second), so SR hears "Pensando…" → "Executando…"
          without the timer spam. */}
      <span role="status" aria-live="polite" className="sr-only">{label}</span>
    </>
  );
}
